import { AddressSearchModal } from '@/components/common/AddressSearchModal';
import { STORE_TYPES } from '@/constants';

interface StoreInfo {
  storeName: string;
  storePhone: string;
  address: string;
  detailAddress: string;
  foodTypes: string[];
  customFoodType: string;
  representativeMenus: string[];
}

interface StoreInfoFormProps {
  formData: StoreInfo;
  onFormChange: (field: keyof StoreInfo, value: string | string[]) => void;
  onMenuChange: (index: number, value: string) => void;
  onFoodTypeChange: (type: string) => void;
  onAddressSelected: (addressData: { roadAddress: string; jibunAddress: string; zonecode: string; }) => void;
  showAddressModal: boolean;
  onShowAddressModal: (show: boolean) => void;
  className?: string;
}

export function StoreInfoForm({ 
  formData, 
  onFormChange,
  onMenuChange,
  onFoodTypeChange,
  onAddressSelected,
  showAddressModal,
  onShowAddressModal,
  className = '' 
}: StoreInfoFormProps) {
  
  const handleInputChange = (field: keyof StoreInfo, value: string) => {
    if (field === 'storePhone') {
      value = value.replace(/[^0-9]/g, '');
    }
    onFormChange(field, value);
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* 기본 매장 정보 */}
      <div className="space-y-6">
        <h3 className="text-[20px] font-semibold text-[#222222]">기본 정보</h3>
        
        {/* 매장명 */}
        <div>
          <label className="block text-[18px] font-medium text-[#222222] mb-3">
            매장명
          </label>
          <input
            type="text"
            value={formData.storeName}
            onChange={(e) => handleInputChange('storeName', e.target.value)}
            placeholder="매장명을 입력해주세요"
            className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
          />
        </div>

        {/* 매장 전화번호 */}
        <div>
          <label className="block text-[18px] font-medium text-[#222222] mb-3">
            매장 전화번호
          </label>
          <input
            type="tel"
            value={formData.storePhone}
            onChange={(e) => handleInputChange('storePhone', e.target.value)}
            placeholder="예시) 0212345678"
            className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
          />
        </div>

        {/* 매장 주소 */}
        <div>
          <label className="block text-[18px] font-medium text-[#222222] mb-3">
            매장 주소
          </label>
          <div className="space-y-3">
            <div className="flex gap-4">
              <input
                type="text"
                value={formData.address}
                readOnly
                placeholder="주소 검색 버튼을 클릭해주세요"
                className="flex-1 h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] bg-gray-50 placeholder:text-[#606060]"
              />
              <button
                type="button"
                onClick={() => onShowAddressModal(true)}
                className="px-6 py-3 bg-[#4CD964] text-white font-semibold text-[16px] rounded-lg hover:bg-[#45B85C] transition-colors whitespace-nowrap"
              >
                주소 검색
              </button>
            </div>
            
            {formData.address && (
              <input
                type="text"
                value={formData.detailAddress}
                onChange={(e) => handleInputChange('detailAddress', e.target.value)}
                placeholder="상세 주소를 입력해주세요 (선택)"
                className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
              />
            )}
          </div>
        </div>
      </div>

      {/* 음식 종류 */}
      <div className="space-y-6">
        <h3 className="text-[20px] font-semibold text-[#222222]">음식 종류</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {STORE_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => onFoodTypeChange(type.label)}
              className={`p-3 rounded-lg border-2 text-[16px] font-medium transition-all ${
                formData.foodTypes.includes(type.label)
                  ? 'border-[#51C854] bg-green-50 text-[#51C854]'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-[#51C854] hover:bg-green-50'
              }`}
            >
              {type.label}
            </button>
          ))}
          
          {/* 기타 버튼 */}
          <button
            type="button"
            onClick={() => onFoodTypeChange('기타')}
            className={`p-3 rounded-lg border-2 text-[16px] font-medium transition-all ${
              formData.foodTypes.includes('기타')
                ? 'border-[#51C854] bg-green-50 text-[#51C854]'
                : 'border-gray-300 bg-white text-gray-700 hover:border-[#51C854] hover:bg-green-50'
            }`}
          >
            기타
          </button>
        </div>

        {/* 기타 음식 종류 입력 */}
        {formData.foodTypes.includes('기타') && (
          <input
            type="text"
            value={formData.customFoodType}
            onChange={(e) => handleInputChange('customFoodType', e.target.value)}
            placeholder="기타 음식 종류를 입력해주세요"
            className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
          />
        )}
      </div>

      {/* 대표 메뉴 */}
      <div className="space-y-6">
        <h3 className="text-[20px] font-semibold text-[#222222]">대표 메뉴 (최대 3개)</h3>
        <div className="space-y-4">
          {formData.representativeMenus.map((menu, index) => (
            <div key={index}>
              <input
                type="text"
                value={menu}
                onChange={(e) => onMenuChange(index, e.target.value)}
                placeholder={`대표메뉴 ${index + 1}${index === 0 ? ' (필수)' : ' (선택)'}`}
                className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* 주소 검색 모달 */}
      <AddressSearchModal
        isOpen={showAddressModal}
        onAddressSelected={onAddressSelected}
        onClose={() => onShowAddressModal(false)}
      />
    </div>
  );
}